import { Activity as ActivityIcon, Sparkles } from "lucide-react";
import React, { forwardRef } from "react";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { formatDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";
import type { OpportunityStageTransitionRowProps } from "@/pages/accounts/account-explorer/touchpoints-table/types";
import { GutterCell } from "./table-cells";

/**
 * Renders a row for an opportunity stage transition
 */
const OpportunityStageTransitionRowInner = forwardRef<
  HTMLTableRowElement,
  OpportunityStageTransitionRowProps
>(
  (
    {
      transition,
      opportunityName,
      index,
      isFirstInThread,
      isLastInThread,
      isInThread,
      showThreadLine,
      onMilestoneAnalysisClick,
    },
    ref,
  ) => {
    // Check if this transition has an associated milestone analysis
    const hasMilestoneAnalysis = !!transition.MilestoneAnalysis;

    // Handler for sparkles button click
    const handleMilestoneAnalysisClick = (
      event: React.MouseEvent<HTMLButtonElement>,
    ) => {
      event.stopPropagation();
      if (
        hasMilestoneAnalysis &&
        transition.MilestoneAnalysis &&
        onMilestoneAnalysisClick
      ) {
        onMilestoneAnalysisClick(transition.MilestoneAnalysis);
      }
    };

    return (
      <TableRow
        data-index={index}
        className={cn(
          "relative h-[40px] hover:bg-muted",
          hasMilestoneAnalysis
            ? "bg-muted border-l-2 border-l-supporting-yellow"
            : "bg-muted",
        )}
        data-row-id={`opportunity-transition-${
          transition.opportunityId
        }-${new Date(transition.timestamp).getTime()}`}
        data-timestamp={transition.timestamp}
        ref={ref}
      >
        <GutterCell
          isFirstInThread={isFirstInThread}
          isLastInThread={isLastInThread}
          isInThread={isInThread}
          showThreadLine={showThreadLine}
        />
        <TableCell
          colSpan={8}
          className="py-1 px-2"
          data-column-id="opportunity-transition"
        >
          <div className="pointer-events-none sticky left-1/2 -translate-x-1/2 flex items-center justify-center w-max max-w-[90vw] z-10">
            <div className="flex items-center gap-8 text-xs text-muted-foreground pointer-events-auto">
              <div className="flex items-center gap-2 min-w-0">
                <ActivityIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="font-medium truncate" title={opportunityName}>
                  {opportunityName}
                </span>
                <span className="flex-shrink-0">moved to</span>
                <span
                  className="font-medium truncate"
                  title={transition.event_name}
                >
                  {transition.event_name}
                </span>
                <span className="text-xs text-muted-foreground/50 flex-shrink-0">
                  {formatDate(transition.timestamp)}
                </span>
              </div>
              {/* Milestone Analysis indicator */}
              {hasMilestoneAnalysis && (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 bg-card border-warning text-warning hover:bg-warning hover:text-warning-foreground"
                  onClick={handleMilestoneAnalysisClick}
                >
                  <Sparkles className="h-3 w-3" />
                  <span>Milestone Analysis</span>
                </Button>
              )}
            </div>
          </div>
        </TableCell>
      </TableRow>
    );
  },
);

export const OpportunityStageTransitionRow = React.memo(
  OpportunityStageTransitionRowInner,
);
